import { useCallback } from 'react';
import { toast } from 'sonner';
import { getSpecificationText, getTransitionFunctionText, buildTransitionTable } from '../utils/automataHelpers';

/**
 * Hook for all clipboard copy operations.
 * Returns functions for copying rich text (HTML + plain text fallback).
 */
export function useClipboard(automata, currentAutomaton) {
  // Core: write both text/html and text/plain to clipboard
  const copyRichText = useCallback((html, plainText, label) => {
    try {
      const blob = new Blob([html], { type: 'text/html' });
      const textBlob = new Blob([plainText], { type: 'text/plain' });
      navigator.clipboard.write([
        new ClipboardItem({ 'text/html': blob, 'text/plain': textBlob })
      ]).then(() => {
        toast.success(`${label} copied to clipboard`);
      }).catch(() => {
        navigator.clipboard.writeText(plainText).then(() => {
          toast.success(`${label} copied to clipboard`);
        }).catch(() => toast.error('Failed to copy'));
      });
    } catch {
      navigator.clipboard.writeText(plainText).then(() => {
        toast.success(`${label} copied to clipboard`);
      }).catch(() => toast.error('Failed to copy'));
    }
  }, []);

  // Build HTML transition table for a given automaton
  const buildTransitionHtml = useCallback((a) => {
    const { states, transitions } = a;
    if (!states?.length || !transitions?.length) return { html: '<p>No transitions</p>', plain: 'No transitions' };

    const { lookup, columns } = buildTransitionTable(states, transitions);
    if (columns.length === 0) return { html: '<p>No transitions</p>', plain: 'No transitions' };

    const headerCells = columns.map(s =>
      `<th style="text-align:center;padding:4px 8px;border:1px solid #ccc">${s}</th>`
    ).join('');

    const bodyRows = states.map(state => {
      const label = state.name + (state.is_initial ? ' (initial)' : '') + (state.is_final ? ' (final)' : '');
      const cells = columns.map(s => {
        const d = lookup[`${state.id}|${s}`];
        return `<td style="text-align:center;padding:4px 8px;border:1px solid #ccc">${d ? `{${[...d].join(',')}}` : '—'}</td>`;
      }).join('');
      return `<tr><td style="font-weight:bold;padding:4px 8px;border:1px solid #ccc">${label}</td>${cells}</tr>`;
    }).join('');

    const html = `<table style="border-collapse:collapse"><thead><tr><th style="text-align:left;padding:4px 8px;border:1px solid #ccc">δ</th>${headerCells}</tr></thead><tbody>${bodyRows}</tbody></table>`;
    const plain = getTransitionFunctionText(a) || 'No transitions';
    return { html, plain };
  }, []);

  const copyLanguageDescription = useCallback(() => {
    if (!currentAutomaton) return;
    const desc = currentAutomaton.language_description || '';
    const html = `<h2>Language Description</h2><p>${desc || '<em>No description</em>'}</p>`;
    copyRichText(html, desc || 'No description', 'Language description');
  }, [currentAutomaton, copyRichText]);

  const copySpecification = useCallback(() => {
    if (!currentAutomaton) return;
    const s = currentAutomaton;
    const q = s.states.length > 0 ? `{${s.states.map(st => st.name).join(', ')}}` : '∅';
    const sigma = s.alphabet.length > 0 ? `{${s.alphabet.join(', ')}}` : '∅';
    const q0 = s.states.find(st => st.is_initial)?.name || '—';
    const finals = s.states.filter(st => st.is_final).map(st => st.name);
    const f = finals.length > 0 ? `{${finals.join(', ')}}` : '∅';
    const html = `<h2>Specification</h2><table><tr><td><strong>Q</strong></td><td>= ${q}</td></tr><tr><td><strong>Σ</strong></td><td>= ${sigma}</td></tr><tr><td><strong>q₀</strong></td><td>= ${q0}</td></tr><tr><td><strong>F</strong></td><td>= ${f}</td></tr></table>`;
    copyRichText(html, getSpecificationText(currentAutomaton), 'Specification');
  }, [currentAutomaton, copyRichText]);

  const copyTransitionFunction = useCallback(() => {
    if (!currentAutomaton) return;
    const { html: transHtml, plain: transPlain } = buildTransitionHtml(currentAutomaton);
    const html = `<h2>Transition Function</h2>${transHtml}`;
    copyRichText(html, transPlain, 'Transition function');
  }, [currentAutomaton, copyRichText, buildTransitionHtml]);

  const copyAutomatonToClipboard = useCallback((automatonId) => {
    const a = automatonId === currentAutomaton?.automaton_id
      ? currentAutomaton
      : automata.find(x => x.automaton_id === automatonId);
    if (!a) return;

    const specText = getSpecificationText(a);
    const q = a.states.length > 0 ? `{${a.states.map(s => s.name).join(', ')}}` : '∅';
    const sigma = a.alphabet.length > 0 ? `{${a.alphabet.join(', ')}}` : '∅';
    const q0 = a.states.find(s => s.is_initial)?.name || '—';
    const finals = a.states.filter(s => s.is_final).map(s => s.name);
    const f = finals.length > 0 ? `{${finals.join(', ')}}` : '∅';
    const specHtml = `<p><strong>Q</strong> = ${q}<br><strong>Σ</strong> = ${sigma}<br><strong>q₀</strong> = ${q0}<br><strong>F</strong> = ${f}</p>`;

    const { html: transHtml, plain: transPlain } = buildTransitionHtml(a);
    const desc = a.language_description || '';

    const html = `<h1>${a.name}</h1><h2>Language Description</h2><p>${desc || '<em>No description</em>'}</p><h2>Specification</h2>${specHtml}<h2>Transition Function</h2>${transHtml}`;
    const plain = `${a.name}\n\nLanguage Description\n${desc || 'No description'}\n\nSpecification\n${specText}\n\nTransition Function\n${transPlain}`;
    copyRichText(html, plain, 'Automaton');
  }, [automata, currentAutomaton, copyRichText, buildTransitionHtml]);

  return {
    copyLanguageDescription,
    copySpecification,
    copyTransitionFunction,
    copyAutomatonToClipboard,
  };
}
